import { useMemo } from 'react';
import { useTheme, Box, Typography, Card, CardContent } from '@mui/material';
import { ResponsivePie } from '@nivo/pie';
import { ResponsiveBar } from '@nivo/bar';

const EmployeeCharts = ({ pieData, statusPieData, barData, rankBarData, homeBaseBarData }) => {
  const theme = useTheme();

  const nivoTheme = useMemo(() => ({
    axis: {
      ticks: { text: { fill: theme.palette.text.secondary, fontSize: 11 } },
      legend: { text: { fill: theme.palette.text.primary } },
    },
    grid: { line: { stroke: theme.palette.divider, strokeWidth: 0.5, strokeDasharray: '3 3' } },
    legends: { text: { fill: theme.palette.text.primary } },
    labels: { text: { fill: theme.palette.text.primary, fontSize: 12 } },
    totals: { text: { fill: theme.palette.text.primary, fontSize: 12 } },
    tooltip: {
      container: {
        background: theme.palette.background.paper,
        color: theme.palette.text.primary,
        borderRadius: 2,
        padding: '8px 12px',
        fontSize: 12,
      },
    },
  }), [theme]);

  const PIE_COMMON = {
    innerRadius: 0.45, padAngle: 0.7, cornerRadius: 6,
    sortByValue: true,
    colors: { scheme: 'set2' },
    activeOuterRadiusOffset: 8,
    borderWidth: 1,
    borderColor: { from: 'color', modifiers: [['darker', 0.2]] },
    arcLinkLabelsSkipAngle: 5,
    arcLinkLabelsDiagonalLength: 10,
    arcLinkLabelsStraightLength: 12,
    arcLinkLabelsTextColor: theme.palette.text.primary,
    arcLinkLabelsThickness: 2,
    arcLinkLabelsColor: { from: 'color' },
    arcLabelsSkipAngle: 5,
    arcLabel: (e) => `${e.value}`,
    arcLabelsTextColor: { from: 'color', modifiers: [['brighter', 3]] },
    theme: nivoTheme,
    legends: [{
      anchor: 'bottom', direction: 'row', translateY: 40,
      itemWidth: 90, itemHeight: 18, symbolSize: 12, symbolShape: 'circle',
      itemTextColor: theme.palette.text.primary,
    }],
  };

  const BAR_COMMON = {
    keys: ['count'],
    padding: 0.3,
    colors: { scheme: 'set2' },
    borderColor: { from: 'color', modifiers: [['darker', 1.5]] },
    axisLeft: { tickSize: 0, tickPadding: 5 },
    enableTotals: true,
    totalsOffset: 6,
    labelSkipWidth: 12,
    labelSkipHeight: 12,
    theme: nivoTheme,
  };

  const barMargin = { top: 20, right: 20, bottom: 20, left: 20 };
  const barAxisBottom = { tickRotation: 0, tickSize: 0 };

  return (
    <>
      {/* Employee Type + Employment Status */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
        <Card>
          <CardContent>
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Employee Type</Typography>
            <Box height={260}>
              <ResponsivePie data={pieData} margin={{ top: 20, right: 100, bottom: 50, left: 100 }} {...PIE_COMMON} />
            </Box>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Employment Status</Typography>
            <Box height={260}>
              <ResponsivePie data={statusPieData} margin={{ top: 20, right: 100, bottom: 50, left: 100 }} {...PIE_COMMON} />
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* By Department */}
      <Card>
        <CardContent>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>By Department</Typography>
          <Box height={300}>
            <ResponsiveBar
              {...BAR_COMMON}
              data={barData} indexBy="dept"
              margin={barMargin}
              axisBottom={barAxisBottom}
              labelTextColor={theme.palette.text.primary}
              totalsTextColor={theme.palette.text.primary}
            />
          </Box>
        </CardContent>
      </Card>

      {/* By Rank + By Home Base */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
        <Card>
          <CardContent>
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>By Rank</Typography>
            <Box height={280}>
              <ResponsiveBar
                {...BAR_COMMON}
                data={rankBarData} indexBy="rank"
                margin={barMargin}
                axisBottom={barAxisBottom}
                labelTextColor={theme.palette.text.primary}
                totalsTextColor={theme.palette.text.primary}
              />
            </Box>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>By Home Base</Typography>
            <Box height={280}>
              <ResponsiveBar
                {...BAR_COMMON}
                data={homeBaseBarData} indexBy="base"
                margin={barMargin}
                axisBottom={barAxisBottom}
                labelTextColor={theme.palette.text.primary}
                totalsTextColor={theme.palette.text.primary}
              />
            </Box>
          </CardContent>
        </Card>
      </Box>
    </>
  );
};

export default EmployeeCharts;
